import type { QueryKey } from "@tanstack/react-query";

import type {
  AbandonedQuery,
  AdsAllocationQuery,
  AdsCampaignFlagsQuery,
  AdsImportListQuery,
  AdsMappingListQuery,
  CustomerQuery,
  EmailDraftListQuery,
  EmailMessageListQuery,
  ProfitQuery,
  SkuDetailQuery,
  SendTimeSuggestionQuery,
  ReportExportListQuery,
  ReportPreviewRequest,
} from "./types";

/**
 * Central query-key registry. Always invalidate through these helpers so keys
 * cannot drift between mutations and queries.
 */
export const qk = {
  me: ["auth", "me"],
  tenant: ["tenant", "me"],
  tenantSettings: ["tenant", "settings"],
  members: ["tenant", "members"],
  member: (id: string) => ["tenant", "members", id],
  subscription: ["billing", "subscription"],
  billingPlans: ["billing", "plans"],
  paymentMode: ["billing", "payment-mode"],
  proDemo: ["billing", "pro-demo"],
  health: ["system", "health"],
  meta: ["system", "meta"],
  adminTenants: (query?: { q?: string; page?: number; page_size?: number }) =>
    ["admin", "tenants", query ?? {}] as QueryKey,
  adminTenant: (id: string) => ["admin", "tenants", id] as QueryKey,
  stores: ["stores"] as QueryKey,
  store: (id: string) => ["stores", id] as QueryKey,
  syncStatus: (id: string) => ["stores", id, "sync-status"] as QueryKey,
  syncRuns: (id: string, query?: { page?: number; page_size?: number }) =>
    ["stores", id, "sync-runs", query ?? {}] as QueryKey,
  // ---- S5 成本与利润 ----
  profitSummary: (query?: ProfitQuery) => ["profit", "summary", query ?? {}] as QueryKey,
  profitSku: (query?: ProfitQuery) => ["profit", "sku", query ?? {}] as QueryKey,
  profitActions: (query?: { from?: string; to?: string; store_id?: string }) =>
    ["profit", "actions", query ?? {}] as QueryKey,
  profitSkuDetail: (query?: SkuDetailQuery) => ["profit", "sku-detail", query ?? {}] as QueryKey,
  profitTrend: (query?: ProfitQuery) => ["profit", "trend", query ?? {}] as QueryKey,
  profitSnapshots: (query?: ProfitQuery) => ["profit", "snapshots", query ?? {}] as QueryKey,
  // ---- S6 客户价值与复购 ----
  customerList: (query?: CustomerQuery) => ["customers", "list", query ?? {}] as QueryKey,
  customerSummary: (query?: CustomerQuery) => ["customers", "summary", query ?? {}] as QueryKey,
  customerSilent: (query?: CustomerQuery) => ["customers", "silent", query ?? {}] as QueryKey,
  customerAudience: (query?: CustomerQuery) => ["customers", "audience", query ?? {}] as QueryKey,
  customerAbandoned: (query?: AbandonedQuery) =>
    ["customers", "abandoned", query ?? {}] as QueryKey,
  customer: (id: string) => ["customers", "detail", id] as QueryKey,
  // ---- S7 广告花费导入与分摊 ----
  adsImports: (query?: AdsImportListQuery) => ["ads", "imports", query ?? {}] as QueryKey,
  adsImport: (id: string) => ["ads", "imports", id] as QueryKey,
  adsMappings: (query?: AdsMappingListQuery) => ["ads", "mappings", query ?? {}] as QueryKey,
  adsCampaignFlags: (query?: AdsCampaignFlagsQuery) =>
    ["ads", "campaign-flags", query ?? {}] as QueryKey,
  adsAllocation: (query?: AdsAllocationQuery) => ["ads", "allocation", query ?? {}] as QueryKey,
  // ---- S8 AI 邮件助手 ----
  emailDrafts: (query?: EmailDraftListQuery) => ["ai", "drafts", query ?? {}] as QueryKey,
  emailDraft: (id: string) => ["ai", "drafts", id] as QueryKey,
  emailMessages: (query?: EmailMessageListQuery) => ["ai", "messages", query ?? {}] as QueryKey,
  emailMessage: (id: string) => ["ai", "messages", id] as QueryKey,
  aiUsage: (query?: { page?: number; page_size?: number }) =>
    ["ai", "usage", query ?? {}] as QueryKey,
  aiSendTime: (query?: SendTimeSuggestionQuery) => ["ai", "send-time", query ?? {}] as QueryKey,
  // ---- S9 报表中心与导出 ----
  reportMeta: ["reports", "meta"] as QueryKey,
  reportPreview: (body?: ReportPreviewRequest) => ["reports", "preview", body ?? {}] as QueryKey,
  reportExports: (query?: ReportExportListQuery) => ["reports", "exports", query ?? {}] as QueryKey,
  reportExport: (id: string) => ["reports", "exports", id] as QueryKey,
  reportSchedules: ["reports", "schedules"] as QueryKey,
} as const;

/** Everything that belongs to the signed-in tenant; used on logout / tenant switch. */
export const tenantScopedKeys = [
  qk.me,
  qk.tenant,
  qk.tenantSettings,
  qk.members,
  qk.subscription,
  qk.stores,
  ["profit"],
  ["customers"],
  ["ads"],
  ["reports"],
];
